import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getProduct,
  createProduct,
  updateProduct,
  type CreateProductDto,
  type UpdateProductDto,
} from '@/api/productApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const TYPE_OPTIONS = ['product', 'service'];
const STATUS_OPTIONS = ['active', 'inactive', 'discontinued'];

interface ProductFormValues {
  name: string;
  description?: string;
  sku: string;
  category?: string;
  type: string;
  unit_price: number;
  currency?: string;
  cost_price: number;
  tax_rate: number;
  status?: string;
  stock_quantity?: number;
  reorder_level: number;
  unit_of_measure?: string;
  weight?: number;
  dimensions?: string;
  supplier_id?: string;
  tags?: string[];
}

export default function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<ProductFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('products', 'write');

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getProduct(id)
      .then((product) => {
        form.setFieldsValue({
          name: product.name,
          description: product.description || undefined,
          sku: product.sku,
          category: product.category || undefined,
          type: product.type,
          unit_price: product.unit_price,
          currency: product.currency || undefined,
          cost_price: product.cost_price,
          tax_rate: product.tax_rate,
          status: product.status,
          reorder_level: product.reorder_level,
          unit_of_measure: product.unit_of_measure || undefined,
          weight: product.weight || undefined,
          dimensions: product.dimensions || undefined,
          supplier_id: product.supplier_id || undefined,
          tags: product.tags?.length ? product.tags : undefined,
        });
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: ProductFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const data: UpdateProductDto = {
          name: values.name,
          description: values.description,
          sku: values.sku,
          category: values.category,
          type: values.type,
          unit_price: values.unit_price,
          currency: values.currency,
          cost_price: values.cost_price,
          tax_rate: values.tax_rate,
          status: values.status,
          reorder_level: values.reorder_level,
          unit_of_measure: values.unit_of_measure,
          weight: values.weight ?? 0,
          dimensions: values.dimensions,
          supplier_id: values.supplier_id,
          tags: values.tags,
        };
        await updateProduct(id, data);
        message.success(t('products.update_success'));
        navigate(`/products/${id}`);
      } else {
        const data: CreateProductDto = {
          name: values.name,
          description: values.description,
          sku: values.sku,
          category: values.category,
          type: values.type,
          unit_price: values.unit_price,
          currency: values.currency,
          cost_price: values.cost_price,
          tax_rate: values.tax_rate,
          stock_quantity: values.stock_quantity ?? 0,
          reorder_level: values.reorder_level,
          unit_of_measure: values.unit_of_measure,
          weight: values.weight ?? 0,
          dimensions: values.dimensions,
          supplier_id: values.supplier_id,
          tags: values.tags,
        };
        await createProduct(data);
        message.success(t('products.create_success'));
        navigate('/products');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('app.error');
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canWrite)
    return <Alert type="warning" message={t('app.forbidden')} />;

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error && isEdit) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/products')}
        >
          {t('app.back')}
        </Button>
        <Alert type="error" message={error} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div>
      <h2>
        {isEdit ? t('products.field.name') + ' — ' + t('app.edit') : t('products.new_product')}
      </h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form<ProductFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        scrollToFirstError
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          label={t('products.field.name')}
          name="name"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('products.field.name')} maxLength={500} />
        </Form.Item>

        <Form.Item label={t('products.field.sku')} name="sku"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('products.field.sku')} maxLength={100} />
        </Form.Item>

        <Form.Item label={t('products.field.type')} name="type" initialValue="product"
          rules={[{ required: true }]}
        >
          <Select
            options={TYPE_OPTIONS.map((v) => ({
              value: v,
              label: t(`products.type.${v}`),
            }))}
          />
        </Form.Item>

        <Form.Item label={t('products.field.category')} name="category">
          <Input placeholder={t('products.field.category')} maxLength={200} />
        </Form.Item>

        <Form.Item label={t('products.field.description')} name="description">
          <TextArea rows={3} placeholder={t('products.field.description')} maxLength={5000} />
        </Form.Item>

        {/* Pricing */}
        <Space size="large" wrap>
          <Form.Item label={t('products.field.unit_price')} name="unit_price" initialValue={0}>
            <InputNumber min={0} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item label={t('products.field.cost_price')} name="cost_price" initialValue={0}>
            <InputNumber min={0} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item label={t('products.field.tax_rate')} name="tax_rate" initialValue={0}>
            <InputNumber min={0} max={1} step={0.01} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label={t('products.field.currency')} name="currency" initialValue="TWD">
            <Input maxLength={10} style={{ width: 100 }} />
          </Form.Item>
        </Space>

        {/* Stock (only on create, read-only note on edit) */}
        <Space size="large" wrap>
          {!isEdit && (
            <Form.Item label={t('products.field.stock_quantity')} name="stock_quantity" initialValue={0}>
              <InputNumber min={0} style={{ width: 150 }} />
            </Form.Item>
          )}
          <Form.Item label={t('products.field.reorder_level')} name="reorder_level" initialValue={0}>
            <InputNumber min={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label={t('products.field.unit_of_measure')} name="unit_of_measure">
            <Input maxLength={50} style={{ width: 150 }} />
          </Form.Item>
        </Space>

        {isEdit && (
          <Form.Item label={t('products.field.status')} name="status">
            <Select
              options={STATUS_OPTIONS.map((v) => ({
                value: v,
                label: t(`products.status.${v}`),
              }))}
            />
          </Form.Item>
        )}

        {/* Physical */}
        <Space size="large" wrap>
          <Form.Item label={t('products.field.weight')} name="weight">
            <InputNumber min={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item label={t('products.field.dimensions')} name="dimensions">
            <Input maxLength={200} style={{ width: 250 }} placeholder="L x W x H" />
          </Form.Item>
        </Space>

        <Form.Item label={t('products.field.supplier_id')} name="supplier_id">
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item label={t('products.field.tags')} name="tags">
          <Select mode="tags" placeholder={t('products.field.tags')} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button
              onClick={() =>
                navigate(isEdit ? `/products/${id}` : '/products')
              }
            >
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
